import React from "react";
import * as PropTypes from "prop-types";
import {getFunctionIconUrl, getFunctionTypeLabel} from "../../root/root.utils";
import {formatDateStr, generateKey} from "../../../utils";
import {IconButton, Grid} from "@mui/material";
import TablePagination from '@mui/material/TablePagination';
import {useTranslation} from "react-i18next";
import {
    hasPermission,
    hasPermissionToHandleMunicipality,
    PERMISSION_EDIT_ANY_FUNCTIONS,
    PERMISSION_EDIT_OWN_FUNCTIONS,
    PERMISSION_EDIT_ANY_MUNICIPALITY_FUNCTION_TEMPLATE
} from "../../../permissions";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";


const CompFunctionCards = (props) => {
    const {
        functions = [],
        actions = [],
        rowClick,
        changePage,
        authenticatedUser = {},
        updateRowsPerPage,
        state,
        municipalities
    } = props;

    const {t} = useTranslation('translation');

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value);
        localStorage.setItem("rowsPerPage", newRowsPerPage);
        updateRowsPerPage(newRowsPerPage);
    };

    const handleClick = (event, callback, row) => {
        event.preventDefault()
        const data = {...row}
        callback(event, data);
    };

    const getActionButtons = (row) => {
        const actionButtons = [];
        let hasPerm = false;
        if(hasPermissionToHandleMunicipality(row.shareEventMunicipalityId ? row.shareEventMunicipalityId : row.municipalityId, authenticatedUser) ||
        hasPermission(PERMISSION_EDIT_ANY_MUNICIPALITY_FUNCTION_TEMPLATE, authenticatedUser)){
            if (hasPermission(PERMISSION_EDIT_ANY_FUNCTIONS, authenticatedUser)) {
                hasPerm = true;
            } else if (hasPermission(PERMISSION_EDIT_OWN_FUNCTIONS, authenticatedUser)) {
                hasPerm = row.creatorId === authenticatedUser.id;
                row.organizers.forEach((organizer) => {
                    if (organizer.id === authenticatedUser.id) {
                        hasPerm = true;
                    }
                })
            }
        }

        if (!hasPerm) return actionButtons;

        actions.forEach(function (button) {
            const label = 'label' in button ? button.label : "";
            const key = generateKey("editButton");
            const className = 'className' in button ? button.className : "";
            const onClick = 'onClick' in button ? button.onClick : () => {
            };

            if (button.id === 'copy' && row.shareEventId) {
                return; //no copying of shared events
            }

            actionButtons.push(
                <button key={key} onClick={(event) => handleClick(event, onClick, row)} className={className}>
                    <div className={'editButton-text'}>{label}</div>
                </button>
            )

        });
        return actionButtons;
    };

    const getFunctionSharedInformation = (row) => {
        if (row.shared && row.shareEventMunicipalityId) {
            var m = municipalities.find(m => m.id === row.municipalityId);
            if (m) {
                return t('function.shared_with')+m.name;
            }
            else {
                return t('function.shared_with');
            }
        }
        else if (row.shared) {
            return t('function.shared_event');
        }
        return "";
    }

    const organizersToString = (organizers) => {
        if (organizers.length > 2) {
            return organizers[0].firstName+" "+organizers[0].lastName+", "+organizers[1].firstName+" "+organizers[1].lastName+"...";
        }
        else if (organizers.length === 2) {
            return organizers[0].firstName+" "+organizers[0].lastName+", "+organizers[1].firstName+" "+organizers[1].lastName;
        }
        else if (organizers.length === 1) {
            return organizers[0].firstName+" "+organizers[0].lastName
        }
        return "";
    }

    return (
        <Grid container spacing={2} className={'CompFunctionCards-container'}>
            <div className={'CompFunctionCards-row CompFunctionCards-header'}>
                <div className={'cell-image'}>
                </div>
                <div className={'cell-content'}>
                    <div className={'information'}>{t("function.card_information")}</div>
                    <div className={'organizers'}>{t("function.card_organizers")}</div>
                    <div className={'participants'}>{t("function.card_participants")}</div>
                </div>
                <div className={'cell-right'}>
                    <div className={'dates'}>{t("function.card_dates")}</div>
                </div>
            </div>
            {functions.map((row, rowIndex) => (
                <Grid className={'CompFunctionCards-row CompFunctionCards-verticalAlign'} key={`card-${rowIndex}`}>
                    <div className={'cell-image'} onClick={e => rowClick(e, row)}>

                        <img className="action-icon-img"
                             src={process.env.PUBLIC_URL + getFunctionIconUrl(row)}
                             alt={t("function.card_image_alt")}/>
                    </div>
                    <div className={'cell-content'} onClick={e => rowClick(e, row)}>
                        <div className={'information'}>
                            <div className={'title'}>{row.name}</div>
                            <div className={'desc'}>{getFunctionTypeLabel(row.eventType)+getFunctionSharedInformation(row)}</div>
                        </div>
                        <div className={'organizers'}>
                            {organizersToString(row.organizers)}
                        </div>
                        <div className={'participants'}>
                            {row.participants ?? 0}
                        </div>
                    </div>

                    <div className={'cell-right CompFunctionCards-verticalAlign'}>
                        <div className={'dates'} onClick={e => rowClick(e, row)}>
                            <div>{formatDateStr(row.startDatetime) + " - "}</div>
                            <div>{formatDateStr(row.endDatetime)}</div>
                        </div>
                        <div className={'actions'}>
                            {getActionButtons(row)}
                        </div>
                    </div>
                </Grid>
            ))}
            <div className={'CompFunctionCards-footer'}>
            <TablePagination
                    labelRowsPerPage={t("components.rows_per_page")}
                    sx={{ 
                        "& .MuiTablePagination-displayedRows": { 
                            display: "none" 
                        },
                        "& .MuiTablePagination-select": {
                            border: "1px solid #ccc",
                            borderRadius: "5px",
                            marginRight: "0px"
                        }
                    }}
                    component="div"
                    nextIconButtonProps={{ style: { display: "none" } }}
                    backIconButtonProps={{ style: { display: "none" } }}
                    count={-1}
                    rowsPerPageOptions={[10, 25, 100]}
                    rowsPerPage={state.limit}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
                <div className="CompTable-paginator">
                    <IconButton
                        onClick={() => changePage(false)}
                        aria-label={t('components.previous_page')}
                        size="large">
                        <KeyboardArrowLeft/>
                    </IconButton>
                    <IconButton
                        onClick={() => changePage(true)}
                        aria-label={t('components.next_page')}
                        size="large">
                        <KeyboardArrowRight/>
                    </IconButton>
                </div>
            </div>
        </Grid>
    );
}

CompFunctionCards.propTypes = {
    functions: PropTypes.array,
    rowClick: PropTypes.func,
    changePage: PropTypes.func,
    actions: PropTypes.array,
    authenticatedUser: PropTypes.object
};

export {
    CompFunctionCards,
}